'use client';

import { Check, RefreshCw } from 'lucide-react';
import { useCadrinoCopy } from '@/lib/cadrino-landing/useCadrinoCopy';
import { useLandingLanguage } from '@/hooks/useLandingLanguage';
import { cn } from '@/lib/utils';

function StatusDot({ online }: { online: boolean }) {
  return (
    <span
      className={cn(
        'inline-block h-2 w-2 rounded-full',
        online ? 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.5)]' : 'bg-slate-500'
      )}
    />
  );
}

export function DevicesMock() {
  const c = useCadrinoCopy();
  const { language } = useLandingLanguage();
  const rows = [
    {
      name: language === 'en' ? 'Main entrance' : 'Glavni ulaz',
      site: 'Novi Sad',
      online: true,
      sync: '2 min',
    },
    {
      name: language === 'en' ? 'Office' : 'Kancelarija',
      site: 'Beograd',
      online: true,
      sync: '5 min',
    },
    {
      name: language === 'en' ? 'Warehouse' : 'Magacin',
      site: 'Niš',
      online: false,
      sync: c.mock.queue.dash,
    },
    {
      name: language === 'en' ? 'Retail store' : 'Poslovnica',
      site: 'Subotica',
      online: true,
      sync: '1 min',
    },
  ];

  return (
    <table className="w-full text-left text-xs">
      <thead>
        <tr className="border-b border-white/[0.08] text-cadrino-muted">
          {c.mock.devices.cols.map((col) => (
            <th key={col} className="pb-3 pr-4 font-medium">
              {col}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="text-slate-300">
        {rows.map((r) => (
          <tr key={r.name} className="border-b border-white/[0.04]">
            <td className="py-3 pr-4 font-medium text-slate-200">{r.name}</td>
            <td className="py-3 pr-4">{r.site}</td>
            <td className="py-3 pr-4">
              <span className="inline-flex items-center gap-2">
                <StatusDot online={r.online} />
                {r.online ? c.mock.devices.online : c.mock.devices.offline}
              </span>
            </td>
            <td className="py-3 text-cadrino-muted">{r.sync}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function AssignmentsMock() {
  const c = useCadrinoCopy();
  const rows = [
    { name: 'Marko P.', a: true, b: false, w: true },
    { name: 'Ana J.', a: true, b: true, w: false },
    { name: 'Stefan K.', a: false, b: true, w: true },
    { name: 'Jelena M.', a: true, b: false, w: false },
  ];

  const Cell = ({ on }: { on: boolean }) =>
    on ? (
      <Check className="h-4 w-4 text-cadrino-accent" />
    ) : (
      <span className="inline-block h-4 w-4 rounded border border-white/10" />
    );

  return (
    <table className="w-full text-left text-xs">
      <thead>
        <tr className="border-b border-white/[0.08] text-cadrino-muted">
          {c.mock.assignments.cols.map((col) => (
            <th key={col} className="pb-3 pr-4 font-medium">
              {col}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.name} className="border-b border-white/[0.04]">
            <td className="py-3 pr-4 font-medium text-slate-200">{r.name}</td>
            <td className="py-3 pr-4">
              <Cell on={r.a} />
            </td>
            <td className="py-3 pr-4">
              <Cell on={r.b} />
            </td>
            <td className="py-3">
              <Cell on={r.w} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

type QueueStatus = 'synced' | 'pending' | 'failed';

export function QueueMock() {
  const c = useCadrinoCopy();
  const { language } = useLandingLanguage();
  const rows: {
    event: string;
    device: string;
    status: QueueStatus;
    action: 'retry' | null;
  }[] = [
    {
      event: language === 'en' ? 'In 08:02' : 'Dolazak 08:02',
      device: language === 'en' ? 'Main entrance' : 'Glavni ulaz',
      status: 'synced',
      action: null,
    },
    {
      event: language === 'en' ? 'Out 16:58' : 'Odlazak 16:58',
      device: language === 'en' ? 'Office' : 'Kancelarija',
      status: 'pending',
      action: 'retry',
    },
    {
      event: language === 'en' ? 'In 06:15' : 'Dolazak 06:15',
      device: language === 'en' ? 'Warehouse' : 'Magacin',
      status: 'failed',
      action: 'retry',
    },
  ];

  const statusLabel = (status: QueueStatus) => c.mock.queue.status[status];

  return (
    <table className="w-full text-left text-xs">
      <thead>
        <tr className="border-b border-white/[0.08] text-cadrino-muted">
          {c.mock.queue.cols.map((col) => (
            <th key={col} className="pb-3 pr-4 font-medium">
              {col}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.event + r.device} className="border-b border-white/[0.04]">
            <td className="py-3 pr-4 text-slate-200">{r.event}</td>
            <td className="py-3 pr-4">{r.device}</td>
            <td className="py-3 pr-4">
              <span
                className={cn(
                  'rounded-md px-2 py-0.5 text-[10px] font-medium',
                  r.status === 'synced' && 'bg-emerald-500/15 text-emerald-400',
                  r.status === 'pending' && 'bg-amber-500/15 text-amber-400',
                  r.status === 'failed' && 'bg-red-500/15 text-red-400'
                )}
              >
                {statusLabel(r.status)}
              </span>
            </td>
            <td className="py-3">
              {r.action === 'retry' ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-cadrino-primary hover:text-blue-400"
                >
                  <RefreshCw className="h-3 w-3" />
                  {c.mock.queue.retry}
                </button>
              ) : (
                <span className="text-cadrino-muted">{c.mock.queue.dash}</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function DashboardChrome({
  children,
  title,
}: {
  children: React.ReactNode;
  title?: string;
}) {
  const c = useCadrinoCopy();
  return (
    <div className="overflow-hidden rounded-2xl border border-white/[0.08] bg-cadrino-surface shadow-[0_1px_2px_rgba(0,0,0,0.24),0_16px_48px_rgba(0,0,0,0.32)]">
      <div className="flex items-center gap-2 border-b border-white/[0.08] px-4 py-3">
        <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
        <span className="ml-2 text-xs text-cadrino-muted">{title ?? c.mock.dashboard.windowTitle}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

